import React from 'react';
import { Container } from '../../../components/Container/Container';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { t } from '../../../locales/translate';
import { useRouterParam } from '../../../model/history';
import { getPageTitle, useMeta } from '../../../model/meta';
import { venuePath, venuesPath } from '../../../routes';
import { useReloadKey } from '../../../util/react-util';
import { graphql } from '../../../generated';
import { Stack } from '../../../components/Stack/Stack';
import { VenueLayoutSeatMap } from './VenueLayoutSeatMap';
import { PricingCategoriesCard } from './PricingCategoriesCard';
import { GeneralInfoCard } from './GeneralInfoCard';
import { DetailsCard } from './DetailsCard';
import { Activities } from '../../../components/Activities/Activities';
import { apollo, useQueryHandler } from '../../../model/graphql';
import { compact } from 'lodash';

const SHOW_VENUE_LAYOUT = graphql(`
  query ShowVenueLayout($id: ID!) {
    venueLayout(id: $id) {
      id
      name
      capacity
      archived
      requiresMapSelection
      importStatus
      venue {
        id
        slug
        name
      }
      permissions {
        id
        update
        destroy
        updateCapacity
        createPricingCategory
      }
    }
  }
`);

export function VenueLayoutDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [activitiesCacheKey, reloadActivities] = useReloadKey();
  const {
    loading,
    data,
    refetch: reloadVenueLayout,
  } = useQueryHandler(SHOW_VENUE_LAYOUT, {
    variables: { id },
    pollWhile: (data) => data?.venueLayout?.importStatus === 'pending',
  });
  const venueLayout = data?.venueLayout;

  useMeta({
    breadcrumbs: compact([
      { title: t.model.venue._name, url: venuesPath() },
      {
        title: getPageTitle(loading, venueLayout?.venue?.name, venueLayout?.venue),
        url: venueLayout?.venue && venuePath(venueLayout.venue.slug),
      },
      !loading && {
        title: getPageTitle(loading, venueLayout?.name, venueLayout),
      },
    ]),
  });

  if (loading && !venueLayout) {
    return <LoadingPage title={t.actions.loading(t.model.venueLayout._name)} />;
  }
  if (!venueLayout) {
    return <Container>{t.errors.itemNotFound(t.model.venueLayout._name)}</Container>;
  }

  return (
    <Container className="venue-details">
      <Stack size="lg">
        <GeneralInfoCard venueLayout={venueLayout} />
        {venueLayout.requiresMapSelection && (
          <VenueLayoutSeatMap
            venueLayoutId={id}
            onChange={() => {
              reloadActivities();
              reloadVenueLayout();
              apollo.refetchQueries({ include: ['VenueLayoutPricingCategories'] }); // temporary for now until this is moved into the new venue editing page
            }}
          />
        )}
        <PricingCategoriesCard
          venueLayoutId={id}
          onChange={() => {
            reloadActivities();
          }}
        />
        <DetailsCard
          venueLayout={venueLayout}
          onChange={() => {
            reloadActivities();
            reloadVenueLayout();
          }}
        />
        <Activities subjectType="venue_layout" subjectId={id} cacheKey={activitiesCacheKey} />
      </Stack>
    </Container>
  );
}
